/**
 * Consented temporal research, on the wire.
 *
 * This is the schema that has to refuse the thing a careless caller would send
 * — the whole task sentence — rather than trust a service to strip it. The
 * existing research contract already rejects free prose in telemetry, and this
 * is the same posture applied to the one place a phrase is deliberately kept.
 *
 * `phrase` is bounded at 120 characters here and by a CHECK constraint in
 * migration 0086. Two guards for one value because they fail at different
 * times: the schema fails the request, the constraint fails a write that
 * reached the database another way.
 *
 * The consent itself is not a new mechanism. It is `research_consents`
 * (migration 0057) under the kind below — one consent surface, one withdrawal
 * path, one audit record. An owner cannot record it for a worker.
 */
import { z } from 'zod';
import { EntityId, IsoDatetime, UserId } from './primitives.js';

/** The separate, optional consent. Declining it changes no Free feature. */
export const TEMPORAL_RESEARCH_CONSENT_KIND = 'temporal_text_research' as const;

/** The longest a temporal phrase may be. Far more than "the Tuesday after
 *  next", far less than a sentence — which is the point. */
export const MAX_TEMPORAL_PHRASE_CHARS = 120 as const;

export const TemporalLabelSignal = z.enum(['accepted', 'corrected', 'rejected']);
export type TemporalLabelSignal = z.infer<typeof TemporalLabelSignal>;

export const TemporalResearchBucket = z.enum(['train', 'validation', 'test']);
export type TemporalResearchBucket = z.infer<typeof TemporalResearchBucket>;

/** The structured proposal, and the correction. No prose in either. */
const StructuredDateValue = z.object({
  date: z.string().nullable(),
  time: z.string().nullable(),
  date_certain: z.boolean(),
  time_certain: z.boolean(),
  end_date: z.string().nullable().optional(),
  end_time: z.string().nullable().optional(),
});

/**
 * `POST /v1/temporal-research/examples`.
 *
 * Deliberately has no `text` field. There is nowhere in this shape to put the
 * task, which is the difference between a policy and a contract: a caller that
 * wanted to send it would have to change this file, in a commit somebody
 * reviews.
 */
export const RecordTemporalExampleRequest = z
  .object({
    phrase: z.string().trim().min(1).max(MAX_TEMPORAL_PHRASE_CHARS),
    phrase_start: z.number().int().nonnegative(),
    phrase_end: z.number().int().positive(),
    locale: z.string().min(2).max(16),
    timezone: z.string().min(1).max(64),
    reference_instant: IsoDatetime,
    parser_version: z.string().max(40),
    proposed: StructuredDateValue,
    corrected: StructuredDateValue.nullable(),
    signal: TemporalLabelSignal,
  })
  .refine((v) => v.phrase_end > v.phrase_start, {
    message: 'phrase_end must be after phrase_start',
    path: ['phrase_end'],
  })
  .refine((v) => v.signal !== 'corrected' || v.corrected !== null, {
    message: 'a corrected label must carry the correction',
    path: ['corrected'],
  });
export type RecordTemporalExampleRequest = z.infer<typeof RecordTemporalExampleRequest>;

/**
 * What a participant may export about themselves.
 *
 * `subject_ref` is included because a person is entitled to see the pseudonym
 * their records are filed under; it grants nothing, since resolving it back to
 * a user requires the subjects table, which no client route reads.
 */
export const TemporalResearchExport = z.object({
  format: z.literal('famanage.temporal-research-export'),
  version: z.literal(1),
  exported_at: IsoDatetime,
  subject_ref: z.string().max(64),
  bucket: TemporalResearchBucket,
  consent: z.object({
    kind: z.literal(TEMPORAL_RESEARCH_CONSENT_KIND),
    status: z.enum(['granted', 'declined', 'withdrawn']),
    effective_at: IsoDatetime,
    withdrawn_at: IsoDatetime.nullable(),
  }),
  examples: z.array(
    z.object({
      id: EntityId.temporalParseExample,
      /** Null once the 90-day phrase expiry has swept it. The structured
       *  record survives to 365 days; the identifying part does not. */
      phrase: z.string().max(MAX_TEMPORAL_PHRASE_CHARS).nullable(),
      locale: z.string(),
      timezone: z.string(),
      reference_instant: IsoDatetime,
      parser_version: z.string(),
      proposed: StructuredDateValue,
      corrected: StructuredDateValue.nullable(),
      signal: TemporalLabelSignal,
      created_at: IsoDatetime,
      phrase_expires_at: IsoDatetime,
      record_expires_at: IsoDatetime,
    }),
  ),
});
export type TemporalResearchExport = z.infer<typeof TemporalResearchExport>;

/**
 * `POST /v1/temporal-research/withdraw`.
 *
 * Takes no subject id. A participant withdraws for themselves and the server
 * resolves the subject from the authenticated actor — an owner passing a
 * worker's id is the exact thing that must be impossible, and the cleanest way
 * to make it impossible is to give the request nowhere to say it.
 */
export const WithdrawTemporalConsentRequest = z.object({
  reason_code: z.string().max(64).default('participant_choice'),
});
export type WithdrawTemporalConsentRequest = z.infer<typeof WithdrawTemporalConsentRequest>;

/** Retention, in days. Two numbers because the phrase and the structured
 *  record expire separately and a single number would sweep the wrong one. */
export const TEMPORAL_RETENTION = {
  minimizedPhraseDays: 90,
  structuredRecordDays: 365,
  /** Withdrawal purges participant-linked data within this many days. */
  withdrawalPurgeDays: 7,
} as const;

export const TemporalResearchStatus = z.object({
  /** Whose status this is. Present so a screen can prove it is showing the
   *  signed-in person's own record and not somebody else's. */
  subject_user_id: UserId,
  consented: z.boolean(),
  withdrawn_at: IsoDatetime.nullable(),
  purge_due_at: IsoDatetime.nullable(),
  example_count: z.number().int().nonnegative(),
});
export type TemporalResearchStatus = z.infer<typeof TemporalResearchStatus>;
